import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useJsApiLoader } from "@react-google-maps/api";
import { useSelector } from "react-redux";

import AvailabilityDialog from "./components/AvailabilityDialog";
import CylinderRail from "./components/CylinderRail";
import FiltersPanel from "./components/FiltersPanel";
import HomeMap from "./components/HomeMap";
import HomeSearchBar from "./components/HomeSearchBar";
import LocationStatus from "./components/LocationStatus";
import NearbyToggle from "./components/NearbyToggle";
import SupplierPanel from "./components/SupplierPanel";

import useHomeData from "./useHomeData";
import useHomeUrlState, { useHomeUrlSync } from "./useHomeUrlState";
import useSupplierSave from "../favourites/useSupplierSave";
import { dismissAvailability, isAvailabilityDismissed } from "./availabilityDismissals";
import { buildFilterSummary } from "./filterSummary";

import useMapLocation from "../search/useMapLocation";
import useSupplierFilters from "../search/useSupplierFilters";
import { radiusToMeters } from "../search/supplierFilters";
import { productPath, supplierPath } from "../detail/detailRoutes";

import useBreakpoint, { BREAKPOINTS } from "../../hooks/use-breakpoint";
import { showErrorToast } from "../../components/Toast";
import { getAuthToken } from "../../lib/api/tokenStore";
import { setStorageItem } from "../../utils/common-utils";
import { GOOGLE_MAP_KEY } from "../../utils/configs";
import { FALLBACK_IP_INFO } from "../../utils/constants";

import "./styles.css";

const MAP_LIBRARIES = ["places"];

const FALLBACK_COORDINATES = {
  latitude: FALLBACK_IP_INFO.location.latitude,
  longitude: FALLBACK_IP_INFO.location.longitude,
};

const DESKTOP_PADDING = { top: 96, right: 64, bottom: 190, left: 64 };
const MOBILE_PADDING = { top: 92, right: 24, bottom: 220, left: 24 };

const HomeRoute = () => {
  const navigate = useNavigate();
  const { width } = useBreakpoint();
  const isDesktop = width >= BREAKPOINTS.lg;

  const { containerOptions, containerTypes } = useSelector(
    (state) => state.publicAPIs
  );

  const { isLoaded } = useJsApiLoader({
    id: "supplier-google-map",
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries: MAP_LIBRARIES,
  });

  const url = useHomeUrlState();

  const { filters, apply, reset, setRadius, clearProducts, activeCount } =
    useSupplierFilters(url.initialFilters);

  const location = useMapLocation({
    initialCoordinates: url.initialCoordinates,
    initialAddress: url.initialAddress,
    mapsReady: isLoaded,
  });

  const usingFallback =
    !location.coordinates &&
    (location.graceElapsed || location.failed || location.permission === "denied");

  const activeCoordinates = location.coordinates || (usingFallback ? FALLBACK_COORDINATES : null);

  const data = useHomeData({ coordinates: activeCoordinates, filters });

  const save = useSupplierSave({ enabled: Boolean(getAuthToken()) });

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [, setDismissals] = useState(0);

  const contextRef = useRef({});
  contextRef.current = { filters, coordinates: activeCoordinates, address: location.address };

  useHomeUrlSync({
    coordinates: location.coordinates,
    address: location.address,
    filters,
    writeState: url.writeState,
  });

  useEffect(() => {
    if (!activeCoordinates) return;

    setStorageItem("marker-location", {
      latitude: activeCoordinates.latitude,
      longitude: activeCoordinates.longitude,
      addressText: location.address,
    });
  }, [activeCoordinates, location.address]);

  const suppliers = data.suppliers;
  const markers = data.markers;

  const openSupplier = useCallback(
    (supplier) => {
      const { filters: current, coordinates } = contextRef.current;

      navigate(
        supplierPath({
          supplierId: supplier.id,
          distanceKm: supplier.distanceKm,
          radiusKm: current.radiusKm,
          coordinates,
        })
      );
    },
    [navigate]
  );

  const openFilters = useCallback(() => setFiltersOpen(true), []);

  const onSelectMarker = useCallback((supplier) => {
    setSelectedId(supplier ? supplier.id : null);
  }, []);

  const onSelectVolume = useCallback(
    (card) => {
      const { filters: current, coordinates } = contextRef.current;

      navigate(
        productPath({
          volume: card.volume,
          radiusKm: current.radiusKm,
          containerOptionId: current.containerOptionId,
          coordinates,
        })
      );
    },
    [navigate]
  );

  const { locate } = location;

  const onLocate = useCallback(async () => {
    const coordinates = await locate();

    if (!coordinates) {
      showErrorToast(
        "We couldn't get your location. Allow location access in your browser and try again."
      );
    }
  }, [locate]);

  const onDismissAvailability = useCallback(() => {
    dismissAvailability(data.signature);
    setDismissals((count) => count + 1);
  }, [data.signature]);

  const onWiden = useCallback(() => {
    if (data.suggestedRadiusKm) setRadius(data.suggestedRadiusKm);
  }, [data.suggestedRadiusKm, setRadius]);

  const filterSummary = useMemo(
    () =>
      buildFilterSummary(filters, {
        containerOptions,
        containerTypes,
        currency: data.suppliers[0]?.currency,
      }),
    [filters, containerOptions, containerTypes, data.suppliers]
  );

  const modalCause =
    data.isReloading || isAvailabilityDismissed(data.signature)
      ? null
      : data.emptyState;

  const purchaseOptions = useMemo(
    () => containerOptions.filter((option) => !option.accessory),
    [containerOptions]
  );

  const listOpen = isDesktop ? panelOpen : sheetOpen;

  const toggleList = useCallback(() => {
    if (isDesktop) setPanelOpen((current) => !current);
    else setSheetOpen((current) => !current);
  }, [isDesktop]);

  const closeList = useCallback(() => {
    if (isDesktop) setPanelOpen(false);
    else setSheetOpen(false);
  }, [isDesktop]);

  const nearbyCount = data.total;
  const nearbyLoading =
    (location.locating && !activeCoordinates) || (data.isResolving && !suppliers.length);

  return (
    <div
      className={`home-screen${
        isDesktop && panelOpen ? " home-screen-desktop" : ""
      }`}
    >
      <div className="home-stage">
        {isLoaded ? (
          <HomeMap
            coordinates={activeCoordinates}
            radiusMeters={radiusToMeters(filters.radiusKm)}
            refitToken={location.refitToken}
            markers={markers}
            selectedId={selectedId}
            onSelect={onSelectMarker}
            onOpen={openSupplier}
            fitPadding={isDesktop ? DESKTOP_PADDING : MOBILE_PADDING}
          />
        ) : (
          <div className="home-map-placeholder" aria-hidden="true" />
        )}

        <div className="home-overlays">
          <div className="home-overlay-top">
            <HomeSearchBar
              address={location.address}
              coordinates={activeCoordinates}
              activeFilters={activeCount}
              onSelectPlace={location.selectPlace}
              onOpenFilters={openFilters}
              mapsReady={isLoaded}
            />

            <LocationStatus
              locating={location.locating && !activeCoordinates}
              usingFallback={usingFallback}
              denied={location.permission === "denied"}
              label={location.locationLabel}
              onLocate={onLocate}
            />
          </div>

          <div className="home-overlay-bottom">
            <div className="home-actions">
              <NearbyToggle
                count={nearbyCount}
                suppliers={suppliers}
                open={listOpen}
                loading={nearbyLoading}
                alwaysShow={isDesktop && !panelOpen}
                onToggle={toggleList}
              />

              <button
                type="button"
                className="home-locate"
                onClick={onLocate}
                disabled={location.locating}
                aria-label="Use my location"
              >
                {location.locating ? (
                  <span className="home-status-spinner" aria-hidden="true" />
                ) : (
                  <img src="/assets/icons/map/current-location.svg" alt="" aria-hidden="true" />
                )}
              </button>
            </div>

            <CylinderRail
              cards={data.cylinderCards}
              loading={data.cylindersFetching && !data.cylinderCards.length}
              activeVolume={null}
              onSelect={onSelectVolume}
            />
          </div>
        </div>
      </div>

      <SupplierPanel
        suppliers={suppliers}
        total={data.total}
        radiusKm={filters.radiusKm}
        isResolving={data.isResolving || data.emptyState === "checking"}
        isReloading={data.isReloading}
        isFetching={data.isFetching}
        asSheet={!isDesktop}
        open={listOpen}
        onClose={closeList}
        onRefresh={data.refetch}
        onOpenSupplier={openSupplier}
        onHoverSupplier={setSelectedId}
        save={save}
      />

      <FiltersPanel
        open={filtersOpen}
        filters={filters}
        containerOptions={purchaseOptions}
        containerTypes={containerTypes}
        asSheet={!isDesktop}
        onClose={() => setFiltersOpen(false)}
        onApply={apply}
        onReset={reset}
      />

      <AvailabilityDialog
        cause={modalCause}
        filters={filters}
        summary={filterSummary}
        nearestKm={data.nearestKm}
        onDismiss={onDismissAvailability}
        onWiden={onWiden}
        onClearFilters={clearProducts}
      />
    </div>
  );
};

export default HomeRoute;
